import { appAnalytics } from 'analytics';
import { ContinueUnfoldPayment, ScreenOpened } from 'analytics/AllEvents';
import { Product } from 'bootstrap/data/product/Product';
import { useAppDispatch, useAppSelector } from 'bootstrap/hooks';
import { RefObject, useEffect, useMemo, useState } from 'react';
import { isApplePayAvailable } from 'subscription/components/Payments/Payments.utlls';
import {
  getMonthDirectProductCode,
  getQuarterDirectProductCode,
  getWeekDirectProductCode,
  getYearDirectProductCode,
} from 'bootstrap/data/product/Product.utils';
import { setPlanType } from 'bootstrap/user/actions/setPlanType';
import { PlanSelectType } from './components/plansection/PlanSection.constants';
import { useFeatures } from 'useFeatures';
import { getQueryVariable } from 'utils';

type Props = {
  defaultProduct: Product;
  products: Map<string, Product>;
  paymentFormRef?: RefObject<HTMLDivElement>;
};

export const useSingleProductLogic = ({
  defaultProduct,
  products,
  paymentFormRef,
}: Props) => {
  const dispatch = useAppDispatch();

  const {
    isFaq,
    isLivenOnboardingFlow,
    isSingleSubscriptionFlow,
    isSingleSubscriptionWithTwoPlansFlow,
  } = useFeatures();

  const {
    authToken,
    hasSubscription,
    isTrialOfferShown,
    currency,
    isPreventBackPaymentOfferConfirmed,
    planType,
  } = useAppSelector((state) => state.user);

  const [expanded, setExpanded] = useState<boolean>(true);
  const [product, setProduct] = useState<Product>(defaultProduct);

  const weekProductCode = useMemo(
    () =>
      getWeekDirectProductCode(
        isLivenOnboardingFlow || isSingleSubscriptionFlow,
      ),
    [product],
  );

  const monthProductCode = useMemo(
    () =>
      getMonthDirectProductCode(
        isLivenOnboardingFlow ||
          (isSingleSubscriptionFlow && !isSingleSubscriptionWithTwoPlansFlow),
      ),
    [product],
  );

  const quarterProductCode = useMemo(
    () =>
      getQuarterDirectProductCode(
        isLivenOnboardingFlow || isSingleSubscriptionFlow,
      ),
    [product],
  );

  const yearProductCode = useMemo(() => getYearDirectProductCode(), [product]);

  useEffect(() => {
    console.log(planType, isSingleSubscriptionFlow, getQueryVariable('flow'));
    if (planType === undefined && isSingleSubscriptionWithTwoPlansFlow) {
      dispatch(setPlanType(PlanSelectType.YEAR));
    } else if (planType === undefined) {
      dispatch(setPlanType(PlanSelectType.MONTH));
    }
  }, [planType]);

  useEffect(() => {
    let productCode = '';

    switch (planType) {
      case PlanSelectType.QUARTER:
        productCode = quarterProductCode;
        break;
      case PlanSelectType.MONTH:
        productCode = monthProductCode;
        break;
      case PlanSelectType.WEEK:
        productCode = weekProductCode;
        break;
      case PlanSelectType.YEAR:
        productCode = yearProductCode;
        break;
      default:
        if (isPreventBackPaymentOfferConfirmed) {
          productCode = 'co-loona-web-ar-q-t7-59';
        } else {
          productCode = defaultProduct.code;
        }
        break;
    }

    if (productCode && products?.get(productCode)) {
      setProduct(products?.get(productCode) as Product);
    } else {
      setProduct(products?.get(defaultProduct.code) as Product);
    }
  }, [
    isPreventBackPaymentOfferConfirmed,
    planType,
    quarterProductCode,
    monthProductCode,
    products,
    product,
  ]);

  useEffect(() => {
    appAnalytics.trackEvent(
      new ScreenOpened('onboardingPayment', {
        isApplePayAvailable: isApplePayAvailable() || {
          reason: {
            paymentRequest: !!window.PaymentRequest,
            applePaySession: !!window.ApplePaySession,
            canMakePayments: !!window.ApplePaySession?.canMakePayments,
          },
        },
      }),
    );
  }, []);

  const scrollToPaymentForm = () => {
    setExpanded(true);
    setTimeout(() => {
      const contentHeightBeforePaymentForm = isFaq ? 70 : 100;
      const paymentFormOffsetTop = paymentFormRef?.current?.offsetTop || 0;
      const topPosition = +(
        paymentFormOffsetTop - contentHeightBeforePaymentForm
      );

      if (paymentFormRef?.current) {
        window.scroll({
          top: topPosition,
          behavior: 'smooth',
        });
      }
    }, 1);
  };

  const handleGetPlanClick = () => {
    appAnalytics.trackEvent(new ContinueUnfoldPayment(''));
    scrollToPaymentForm();
  };

  return {
    product,
    isTrialOfferShown,
    weekProduct: products?.get(weekProductCode) || defaultProduct,
    monthProduct: products?.get(monthProductCode) || defaultProduct,
    quarterProduct: products?.get(quarterProductCode) || defaultProduct,
    yearProduct: products?.get(yearProductCode) || defaultProduct,
    planType,
    hasSubscription,
    authToken,
    expanded,
    currencyCode: currency,
    handleGetPlanClick,
  };
};
